import React, { useRef, useState, type JSX } from 'react';
import { Button } from '@rocco/components/button/button';
import { createSectionScrollOnCollapse } from '../../utils/use-section-scroll-on-collapse';

interface Props {
    title: React.ReactNode;
    tileDoms: JSX.Element[];
}

/**
 * Grid layout for featured products on project details page.
 * - 4 columns
 * - 2 rows shown by default (8 tiles)
 * - "View More" expands to all tiles
 */
export const DetailsPageProductTileGrid = ({ title, tileDoms }: Props) => {
    const [expanded, setExpanded] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const tilesToShow = expanded
        ? tileDoms.length
        : Math.min(tileDoms.length, 8);

    const handleToggle = createSectionScrollOnCollapse(
        expanded,
        setExpanded,
        sectionRef,
    );

    return (
        <>
            <div
                ref={sectionRef}
                className="mb-2 font-display text-20 leading-[100%] font-semibold text-[#3C3C3C]"
            >
                {title ? title : 'Products used'}
            </div>
            <div className="relative w-full border-t border-[#141414]/20">
                <div
                    className="mt-8.5 grid gap-x-[1.1rem] gap-y-18"
                    style={{
                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                    }}
                >
                    {tileDoms.slice(0, tilesToShow).map((tile, idx) => (
                        <div key={idx} className="w-full">
                            <div className="h-full w-full overflow-hidden">
                                {tile}
                            </div>
                        </div>
                    ))}
                </div>

                {tileDoms.length >= 9 && (
                    <div className="mt-30 flex justify-center">
                        <Button
                            onClick={handleToggle}
                            color="primary"
                            size="larger"
                            className="min-w-53.5"
                        >
                            {expanded ? 'View Less' : 'View More'}
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};
